/**
 * THE TRACK: the boat's natural place in the world as a pure function of progression.
 *
 * The sea is one continuous plane. The track is an Archimedean spiral out of the home
 * water, r = A * theta, parametrised by TRUE ARC LENGTH so that "leagues along the
 * track" means leagues, and a pace expressed in leagues per hour is honest.
 *
 * Arc length has a closed form for this spiral:
 *     L(theta) = (A/2) * ( theta*sqrt(1+theta^2) + asinh(theta) )
 * so the forward direction is exact. The inverse is done by bisection with a FIXED
 * iteration count, which makes it deterministic to the last bit rather than merely
 * convergent: the same progression always yields the same position, on any machine,
 * whether the player was watching or not.
 *
 * Nothing here accumulates. There is no integrator, no per-frame step, no stored
 * heading. That is the property that makes an absent player's boat land exactly where
 * a present player's would.
 */

import { SPIRAL_LEAGUES_PER_RADIAN } from './constants';
import type { Degrees, Leagues, Vec2 } from './units';
import { headingDegFromVector, vec2 } from './units';

const A = SPIRAL_LEAGUES_PER_RADIAN;

/** Bisection steps for the arc-length inverse. 64 exhausts double precision. */
const INVERSE_STEPS = 64;

/** Doublings allowed while bracketing the inverse. 2^64 radians is far past any save. */
const BRACKET_DOUBLINGS = 64;

/** Radius of the track at angle theta. Leagues. */
export function spiralRadius(theta: number): Leagues {
  return A * theta;
}

/** Exact arc length from theta = 0 to theta. Leagues. Monotone increasing. */
export function spiralArcLength(theta: number): Leagues {
  const t = theta <= 0 ? 0 : theta;
  return (A / 2) * (t * Math.sqrt(1 + t * t) + Math.asinh(t));
}

/**
 * Inverse of spiralArcLength. Deterministic: fixed bracket doublings then a fixed
 * number of bisection steps, no convergence test, no early exit.
 */
export function thetaAtArcLength(s: Leagues): number {
  if (!Number.isFinite(s) || s <= 0) return 0;

  let hi = 1;
  for (let i = 0; i < BRACKET_DOUBLINGS && spiralArcLength(hi) < s; i += 1) {
    hi *= 2;
  }
  let lo = 0;
  for (let i = 0; i < INVERSE_STEPS; i += 1) {
    const mid = (lo + hi) / 2;
    if (spiralArcLength(mid) < s) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Position on the track at a given angle. */
export function trackPositionAtTheta(theta: number): Vec2 {
  const r = spiralRadius(theta);
  return vec2(r * Math.sin(theta), r * Math.cos(theta));
}

/** Position on the track this many leagues along it from home. */
export function trackPositionAtDistance(s: Leagues): Vec2 {
  return trackPositionAtTheta(thetaAtArcLength(s));
}

/**
 * Direction of travel along the track, as a compass bearing. Closed form: the
 * derivative of the spiral, not a difference of two sampled positions.
 */
export function trackHeadingAtDistance(s: Leagues): Degrees {
  const theta = thetaAtArcLength(s);
  // d/dtheta of (A*theta*sin, A*theta*cos)
  const dx = A * (Math.sin(theta) + theta * Math.cos(theta));
  const dy = A * (Math.cos(theta) - theta * Math.sin(theta));
  return headingDegFromVector(dx, dy);
}

/** Radial spacing between successive coils of the track. Leagues. */
export const TRACK_COIL_SPACING_LEAGUES: Leagues = 2 * Math.PI * A;
